import { useState, useRef, useCallback, useEffect } from "react";

const OVERFLOW_BUTTON_WIDTH = 56; // Approximate width of "··· N" button

interface TabOverflowConfig {
  /** Total number of tabs */
  tabCount: number;
  /** Currently active tab index (for swap logic) */
  activeIndex: number;
  /**
   * Inter-tab gap in px — MUST match the rendered strip's gap (and the measure
   * row's), i.e. the underline tab bar's gap-6 (24px). A wrong value makes the width estimate off, so the strip fails
   * to collapse and overflows.
   */
  gapPx: number;
}

interface TabOverflowResult {
  /** Ref for the outer container to observe its width */
  containerRef: React.RefCallback<HTMLDivElement>;
  /** Ref for the hidden measurement row */
  measureRef: React.RefCallback<HTMLDivElement>;
  /** How many tabs fit in the available space. Equal to tabCount when no overflow. */
  visibleCount: number;
}

/**
 * Measures tab widths via an invisible duplicate row and determines
 * how many fit in the container, accounting for an overflow button.
 *
 * When the active tab would overflow, it gets swapped into the last
 * visible slot by the consumer — this hook only reports visibleCount.
 */
export function useTabOverflow_({
  tabCount,
  activeIndex,
  gapPx,
}: TabOverflowConfig): TabOverflowResult {
  const [visibleCount, setVisibleCount] = useState(tabCount);
  const containerElRef = useRef<HTMLDivElement | undefined>(undefined);
  const measureElRef = useRef<HTMLDivElement | undefined>(undefined);

  const calculate = useCallback(() => {
    const container = containerElRef.current;
    const measure = measureElRef.current;
    if (!container || !measure) {
      setVisibleCount(tabCount);
      return;
    }

    const containerWidth = container.offsetWidth;
    const children = Array.from(measure.children) as HTMLElement[];
    if (children.length === 0) {
      setVisibleCount(tabCount);
      return;
    }

    // Collect each tab's width
    const widths = children.map((el) => el.offsetWidth);

    // Total width if all tabs fit (with gaps)
    const totalWidth =
      widths.reduce((sum, w) => sum + w, 0) + gapPx * (widths.length - 1);

    // All tabs fit — no overflow needed
    if (totalWidth <= containerWidth) {
      setVisibleCount(tabCount);
      return;
    }

    // Reserve space for overflow button + its gap
    const available = containerWidth - OVERFLOW_BUTTON_WIDTH - gapPx;
    let accumulated = 0;
    let count = 0;

    for (let i = 0; i < widths.length; i++) {
      const next = accumulated + widths[i]! + (i > 0 ? gapPx : 0);
      if (next > available) break;
      accumulated = next;
      count++;
    }

    // Always show at least 1 tab
    setVisibleCount(Math.max(1, count));
  }, [tabCount, gapPx]);

  // ResizeObserver on container
  useEffect(() => {
    const el = containerElRef.current;
    if (!el) return;
    const observer = new ResizeObserver(calculate);
    observer.observe(el);
    return () => observer.disconnect();
  }, [calculate]);

  // Recalculate when tab count or active index changes
  useEffect(() => {
    calculate();
  }, [tabCount, activeIndex, calculate]);

  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerElRef.current = node ?? undefined;
      calculate();
    },
    [calculate],
  );

  const measureRef = useCallback(
    (node: HTMLDivElement | null) => {
      measureElRef.current = node ?? undefined;
      calculate();
    },
    [calculate],
  );

  return { containerRef, measureRef, visibleCount };
}
